'use client'

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'
import type { EditorMode } from '@/msgdeskRuntime/contracts/editorActions'
import { MainContent } from '@/deskUiSkeletons/Page/MainContent'
import { useDeskMenu } from '@/deskUiSkeletons/Menu/menuModel'
import {
  legacyHookAttrs,
  type LegacyContractName,
} from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import {
  selectMainHeadingText,
  selectMainState,
  selectRootFeedIds,
  selectSubRenderState,
} from '@/msgdeskRuntime/state'
import { styleFromString } from '@/msgdeskRuntime/legacyBridge/legacyActions'
import { useSubsStore } from '@/state/MsgTreeProvider'
import {
  AnswersPanel,
  MenuSubsPanels,
  MessagesPanel,
  SubsToolbar,
} from '@/deskUiSkeletons/Page/Panels'
import {
  EnterPinForm,
  LoadingOverlay,
  QuickReply,
  ShellOverlays,
} from '@/deskUiSkeletons/Shared/Overlays'
import { PinDialog } from '@/deskUiSkeletons/Shared/PinDialog'
import { WikiMenu } from './WikiMenu'
import { WikiEditorTemplate } from './editor/WikiEditorTemplate'
import { WikiMainAttachments } from './WikiMainAttachments'

type WorkspaceKind = 'main' | 'editor' | 'attachments' | 'favs' | 'trash' | 'drafts'
type TextSize = 'small' | 'standard' | 'large'
type PageWidth = 'standard' | 'wide'
type ColorMode = 'automatic' | 'light' | 'dark'

interface WikiAppearanceState {
  text: TextSize
  width: PageWidth
  color: ColorMode
}

interface WikiContentsItem {
  id: string
  label: string
  level: number
}

const APPEARANCE_KEY = 'msgdesk.wiki.appearance.v1'
const defaultAppearance: WikiAppearanceState = {
  text: 'standard',
  width: 'standard',
  color: 'light',
}

const workspaceByKind: Record<WorkspaceKind, { contract: LegacyContractName; style: string }> = {
  main: { contract: 'workspace.main', style: '' },
  editor: { contract: 'workspace.editor', style: 'height:100%;' },
  attachments: { contract: 'workspace.attachments', style: '' },
  favs: { contract: 'workspace.favs', style: '' },
  trash: { contract: 'workspace.trash', style: '' },
  drafts: { contract: 'workspace.drafts', style: '' },
}

function WorkspaceSection({ kind, children }: { kind: WorkspaceKind; children?: ReactNode }) {
  const { state } = useSubsStore()
  const info = workspaceByKind[kind]
  const visible = state.chromeByContract[info.contract]?.visible !== false
  return (
    <section
      {...legacyHookAttrs(info.contract, `md-wiki-workspace md-wiki-workspace--${kind} min-w-0${visible ? '' : ' hidden'}${kind === 'main' || kind === 'editor' ? ' flex-1' : ''}`)}
      style={{ ...styleFromString(info.style), ...(visible ? {} : { display: 'none' }) }}
      {...(visible ? {} : { 'data-md-react-display': 'none' })}
    >
      {children}
    </section>
  )
}

function useWikiAppearance() {
  const [appearance, setAppearance] = useState<WikiAppearanceState>(defaultAppearance)
  const [loaded, setLoaded] = useState(false)
  const [systemDark, setSystemDark] = useState(false)

  useEffect(() => {
    try {
      const stored = JSON.parse(window.localStorage.getItem(APPEARANCE_KEY) || '{}') as Partial<WikiAppearanceState>
      setAppearance({
        text: stored.text === 'small' || stored.text === 'large' ? stored.text : 'standard',
        width: stored.width === 'wide' ? 'wide' : 'standard',
        color: stored.color === 'automatic' || stored.color === 'dark' ? stored.color : 'light',
      })
    } catch {
      setAppearance(defaultAppearance)
    }
    setLoaded(true)
  }, [])

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const sync = () => setSystemDark(media.matches)
    sync()
    media.addEventListener('change', sync)
    return () => media.removeEventListener('change', sync)
  }, [])

  useEffect(() => {
    if (!loaded) return
    window.localStorage.setItem(APPEARANCE_KEY, JSON.stringify(appearance))
  }, [appearance, loaded])

  return {
    appearance,
    setAppearance,
    dark: appearance.color === 'dark' || (appearance.color === 'automatic' && systemDark),
  }
}

function slug(value: string): string {
  return value
    .toLocaleLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 72) || 'section'
}

function useWikiContents(contentRoot: React.RefObject<HTMLDivElement | null>, active: boolean, syncVersion: number) {
  const [items, setItems] = useState<WikiContentsItem[]>([])

  useEffect(() => {
    if (!active || !contentRoot.current) {
      setItems([])
      return
    }

    const root = contentRoot.current
    const scan = () => {
      const duplicates = new Map<string, number>()
      const next = Array.from(root.querySelectorAll<HTMLElement>('h1, h2, h3, h4, h5, h6'))
        .flatMap((heading): WikiContentsItem[] => {
          const label = heading.textContent?.replace(/\s+/g, ' ').trim() || ''
          if (!label) return []
          const base = slug(label)
          const count = (duplicates.get(base) || 0) + 1
          duplicates.set(base, count)
          const id = `wiki-section-${base}${count > 1 ? `-${count}` : ''}`
          heading.id = id
          return [{ id, label, level: Number(heading.tagName.slice(1)) || 2 }]
        })
      setItems(current => (
        current.length === next.length
        && current.every((item, index) => (
          item.id === next[index]?.id
          && item.label === next[index]?.label
          && item.level === next[index]?.level
        ))
          ? current
          : next
      ))
    }

    scan()
    const observer = new MutationObserver(scan)
    observer.observe(root, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [active, contentRoot, syncVersion])

  return items
}

function WikiParentTrail() {
  const { parents } = useDeskMenu()
  if (!parents.canSee) return null
  const wikiParents = parents.links.filter(parent => parent.msgType === 'wiki').reverse()
  if (!wikiParents.length) return null

  return (
    <nav className="mb-3 text-[13px] text-[var(--wiki-muted)]" aria-label="Wiki ancestry">
      <ol className="flex list-none flex-wrap items-center gap-x-2 gap-y-1 p-0">
        {wikiParents.map((parent, index) => (
          <li key={`${parent.number}:${parent.codeKey}`} className="flex min-w-0 items-center gap-2">
            {index > 0 ? <span aria-hidden="true">›</span> : null}
            <a className="max-w-[32rem] truncate text-[var(--wiki-link)] hover:underline" href={`/${parent.codeKey}`}>
              {parent.subject || parent.label}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  )
}

function ArticleTabs({ articleHref, talkHref }: { articleHref: string; talkHref?: string }) {
  return (
    <nav className="flex min-h-8 items-end gap-5 border-b border-[var(--wiki-border)] text-[14px]" aria-label="Article">
      <a className="border-b-2 border-[var(--wiki-text)] pb-1.5 text-[var(--wiki-text)] no-underline" href={articleHref}>Article</a>
      {talkHref ? (
        <a className="pb-2 text-[var(--wiki-link)] no-underline hover:underline" href={talkHref}>Talk</a>
      ) : (
        <span className="pb-2 text-[var(--wiki-muted)]" aria-disabled="true">Talk</span>
      )}
    </nav>
  )
}

function WikiMainArticle({
  contentRef,
  firstAnswerHref,
}: {
  contentRef: React.RefObject<HTMLDivElement | null>
  firstAnswerHref?: string
}) {
  const { state } = useSubsStore()
  const main = selectMainState(state)
  const title = selectMainHeadingText(state) || 'Wiki'
  const articleHref = main.codeKey ? `/${main.codeKey}` : '#wiki-top'

  return (
    <article id="wiki-top" {...legacyHookAttrs('main.root', 'md-wiki-main')}>
      <WikiParentTrail />
      <header {...legacyHookAttrs('main.header', '')}>
        <div {...legacyHookAttrs('main.avatarFrame', 'hidden')} hidden aria-hidden="true" />
        <span {...legacyHookAttrs('main.nameKeySign', 'hidden')} hidden aria-hidden="true">@</span>
        <div {...legacyHookAttrs('main.emailRegion', 'hidden')} hidden aria-hidden="true" />
        <div {...legacyHookAttrs('main.subjectRegion', '')}>
          <div {...legacyHookAttrs('main.subjectLine', '')}>
            <h1 {...legacyHookAttrs('main.subjectText', 'break-words border-b border-[var(--wiki-border)] pb-1 font-serif text-[29px] font-normal leading-[1.25] text-[var(--wiki-text)]')}>
              {title}
            </h1>
            <span {...legacyHookAttrs('main.authorFrame', 'hidden')} hidden aria-hidden="true">{main.author || main.nameKey || 'Wiki'}</span>
            <span {...legacyHookAttrs('main.datetime', 'hidden')} hidden aria-hidden="true">{main.datetime || ''}</span>
          </div>
          <div {...legacyHookAttrs('main.lineClear', 'hidden')} hidden aria-hidden="true" />
        </div>
      </header>

      <ArticleTabs articleHref={articleHref} talkHref={firstAnswerHref} />

      <div {...legacyHookAttrs('main.quad', 'mt-3')}>
        <div {...legacyHookAttrs('main.body', 'relative')}>
          <p className="mb-4 text-[14px] text-[var(--wiki-muted)]">
            From MsgDesk Wiki, the message encyclopedia
            {main.author || main.nameKey ? <> · {main.author || main.nameKey}</> : null}
            {main.datetime ? <> · {String(main.datetime)}</> : null}
          </p>
          <div ref={contentRef}>
            <div
              {...legacyHookAttrs(
                'main.iframe',
                'md-wiki-article-content break-words leading-[1.65] text-[var(--wiki-text)] [&_a]:text-[var(--wiki-link)] [&_a]:underline-offset-2 [&_blockquote]:border-l-4 [&_blockquote]:border-[var(--wiki-border)] [&_blockquote]:pl-4 [&_h1]:mb-3 [&_h1]:mt-6 [&_h1]:border-b [&_h1]:border-[var(--wiki-border)] [&_h1]:pb-1 [&_h1]:font-serif [&_h1]:text-[1.8em] [&_h1]:font-normal [&_h2]:mb-3 [&_h2]:mt-7 [&_h2]:border-b [&_h2]:border-[var(--wiki-border)] [&_h2]:pb-1 [&_h2]:font-serif [&_h2]:text-[1.5em] [&_h2]:font-normal [&_h3]:mb-2 [&_h3]:mt-5 [&_h3]:text-[1.2em] [&_h3]:font-semibold [&_img]:max-w-full [&_li]:my-1 [&_p]:mb-4 [&_table]:border-collapse [&_td]:border [&_td]:border-[var(--wiki-border)] [&_td]:p-2 [&_th]:border [&_th]:border-[var(--wiki-border)] [&_th]:bg-[var(--wiki-subtle)] [&_th]:p-2',
              )}
            >
              <MainContent />
            </div>
          </div>
        </div>
        <div {...legacyHookAttrs('main.editMount', 'hidden')} hidden aria-hidden="true" />
      </div>
    </article>
  )
}

function WikiContents({
  items,
  mode,
  onHide,
}: {
  items: WikiContentsItem[]
  mode: string
  onHide?: () => void
}) {
  const links = mode === 'msg'
    ? items
    : [{ id: `wiki-${mode}`, label: mode === 'submsges' ? 'Messages' : mode === 'answers' ? 'Answers' : 'Page', level: 2 }]

  return (
    <nav className="sticky top-3 text-[14px]" aria-label="Contents">
      <div className="mb-2 flex items-center justify-between border-b border-[var(--wiki-border)] pb-2">
        <strong>Contents</strong>
        {onHide ? (
          <button
            type="button"
            className="text-[12px] font-normal text-[var(--wiki-link)] hover:underline"
            aria-label="Hide Contents panel"
            aria-controls="md-wiki-contents-panel"
            onClick={onHide}
          >
            hide
          </button>
        ) : null}
      </div>
      <a className="block py-1 font-semibold text-[var(--wiki-text)] no-underline" href="#wiki-top">(Top)</a>
      {links.map(item => (
        <a
          key={item.id}
          className="block py-1 text-[var(--wiki-link)] no-underline hover:underline"
          style={{ paddingLeft: `${Math.max(0, item.level - 2) * 12}px` }}
          href={`#${item.id}`}
        >
          {item.label}
        </a>
      ))}
    </nav>
  )
}

function AppearanceChoice<T extends string>({
  legend,
  value,
  options,
  onChange,
}: {
  legend: string
  value: T
  options: readonly { value: T; label: string }[]
  onChange: (value: T) => void
}) {
  return (
    <fieldset className="border-t border-[var(--wiki-border)] py-3">
      <legend className="mb-2 w-full pt-2 text-[14px] text-[var(--wiki-muted)]">{legend}</legend>
      <div className="flex flex-col gap-2">
        {options.map(option => (
          <label key={option.value} className="flex cursor-pointer items-center gap-2 text-[14px]">
            <input
              type="radio"
              checked={value === option.value}
              onChange={() => onChange(option.value)}
              className="h-[18px] w-[18px] accent-[#36c]"
            />
            {option.label}
          </label>
        ))}
      </div>
    </fieldset>
  )
}

function WikiAppearance({
  appearance,
  setAppearance,
  onHide,
}: {
  appearance: WikiAppearanceState
  setAppearance: React.Dispatch<React.SetStateAction<WikiAppearanceState>>
  onHide?: () => void
}) {
  return (
    <aside className="sticky top-3 text-[var(--wiki-text)]">
      <div className="mb-1 flex items-center justify-between border-b border-[var(--wiki-border)] pb-2 text-[14px]">
        <strong>Appearance</strong>
        {onHide ? (
          <button
            type="button"
            className="text-[12px] font-normal text-[var(--wiki-link)] hover:underline"
            aria-label="Hide Appearance panel"
            aria-controls="md-wiki-appearance-panel"
            onClick={onHide}
          >
            hide
          </button>
        ) : null}
      </div>
      <AppearanceChoice
        legend="Text"
        value={appearance.text}
        options={[
          { value: 'small', label: 'Small' },
          { value: 'standard', label: 'Standard' },
          { value: 'large', label: 'Large' },
        ]}
        onChange={text => setAppearance(current => ({ ...current, text }))}
      />
      <AppearanceChoice
        legend="Width"
        value={appearance.width}
        options={[
          { value: 'standard', label: 'Standard' },
          { value: 'wide', label: 'Wide' },
        ]}
        onChange={width => setAppearance(current => ({ ...current, width }))}
      />
      <AppearanceChoice
        legend="Color"
        value={appearance.color}
        options={[
          { value: 'automatic', label: 'Automatic' },
          { value: 'light', label: 'Light' },
          { value: 'dark', label: 'Dark' },
        ]}
        onChange={color => setAppearance(current => ({ ...current, color }))}
      />
    </aside>
  )
}

function RouteHeading({ id, children }: { id: string; children: ReactNode }) {
  return (
    <h1 id={id} className="mb-5 border-b border-[var(--wiki-border)] pb-1 font-serif text-[29px] font-normal leading-tight text-[var(--wiki-text)]">
      {children}
    </h1>
  )
}

export function WikiPage({
  isEditorOpen = false,
  editorMode,
  settingsOpen,
  onToggleSettings,
  onCloseSettings,
}: {
  isEditorOpen?: boolean
  editorMode?: EditorMode
  settingsOpen: boolean
  onToggleSettings: () => void
  onCloseSettings: () => void
}) {
  const { state } = useSubsStore()
  const mode = state.renderProjection.mode
  const editorActive = isEditorOpen || mode === 'editor'
  const [contentsRailOpen, setContentsRailOpen] = useState(true)
  const [appearanceRailOpen, setAppearanceRailOpen] = useState(true)
  const contentRef = useRef<HTMLDivElement>(null)
  const contents = useWikiContents(contentRef, mode === 'msg', state.syncVersion)
  const { appearance, setAppearance, dark } = useWikiAppearance()
  const main = selectMainState(state)
  const firstAnswerHref = useMemo(() => {
    const firstId = selectRootFeedIds(state, false, 'msg')[0]
    if (firstId === undefined) return undefined
    const first = selectSubRenderState(state, firstId)
    return first?.href || (first?.codeKey ? `/${first.codeKey}` : undefined)
  }, [state])
  const title = selectMainHeadingText(state) || 'Wiki'
  const textClass = appearance.text === 'small'
    ? 'text-[14px]'
    : appearance.text === 'large'
      ? 'text-[18px]'
      : 'text-[16px]'
  const widthClass = appearance.width === 'wide' ? 'max-w-[1900px]' : 'max-w-[1600px]'
  const showContentsRail = !editorActive && contentsRailOpen
  const showAppearanceRail = !editorActive && appearanceRailOpen
  const railColumnsClass = showContentsRail && showAppearanceRail
    ? 'xl:grid-cols-[190px_minmax(0,1fr)_190px] 2xl:grid-cols-[220px_minmax(0,1fr)_220px]'
    : showContentsRail
      ? 'xl:grid-cols-[190px_minmax(0,1fr)] 2xl:grid-cols-[220px_minmax(0,1fr)]'
      : showAppearanceRail
        ? 'xl:grid-cols-[minmax(0,1fr)_190px] 2xl:grid-cols-[minmax(0,1fr)_220px]'
        : 'xl:grid-cols-[minmax(0,1fr)] 2xl:grid-cols-[minmax(0,1fr)]'
  const pageStyle = {
    colorScheme: dark ? 'dark' : 'light',
    '--wiki-bg': dark ? '#101418' : '#ffffff',
    '--wiki-canvas': dark ? '#202122' : '#f8f9fa',
    '--wiki-text': dark ? '#eaecf0' : '#202122',
    '--wiki-muted': dark ? '#a2a9b1' : '#54595d',
    '--wiki-border': dark ? '#72777d' : '#a2a9b1',
    '--wiki-subtle': dark ? '#27292d' : '#f8f9fa',
    '--wiki-link': dark ? '#8ab4f8' : '#36c',
  } as CSSProperties

  return (
    <>
      <ShellOverlays />
      <div
        {...legacyHookAttrs('page.root', `md-wiki-page min-h-screen w-full bg-[var(--wiki-canvas)] text-[var(--wiki-text)] ${textClass}`)}
        style={pageStyle}
      >
        <div {...legacyHookAttrs('page.header', 'bg-[var(--wiki-bg)]')}>
          <div {...legacyHookAttrs('page.headerCell', '')}>
            <WikiMenu
              isEditorOpen={editorActive}
              editorMode={editorMode}
              settingsOpen={settingsOpen}
              onToggleSettings={onToggleSettings}
            />
          </div>
        </div>

        <div {...legacyHookAttrs('page.table', `md-wiki-page-table mx-auto grid min-h-[calc(100vh-72px)] w-full ${widthClass} grid-cols-1 gap-0 bg-[var(--wiki-bg)] px-4 pb-12 pt-5 sm:px-8 xl:gap-10 ${railColumnsClass}`)}>
          <aside id="md-wiki-contents-panel" {...legacyHookAttrs('page.accentCell', showContentsRail ? 'hidden min-w-0 xl:block' : 'hidden')}>
            {showContentsRail ? (
              <WikiContents items={contents} mode={mode} onHide={() => setContentsRailOpen(false)} />
            ) : null}
          </aside>

          <main {...legacyHookAttrs('page.workspaceCell', 'min-w-0 bg-[var(--wiki-bg)]')}>
            {!editorActive && (!contentsRailOpen || !appearanceRailOpen) ? (
              <div className="mb-3 hidden items-center justify-between gap-3 text-[12px] xl:flex">
                <span>
                  {!contentsRailOpen ? (
                    <button
                      type="button"
                      className="rounded-sm border border-[var(--wiki-border)] bg-[var(--wiki-subtle)] px-2 py-1 text-[var(--wiki-link)] hover:underline"
                      aria-label="Show Contents panel"
                      aria-controls="md-wiki-contents-panel"
                      onClick={() => setContentsRailOpen(true)}
                    >
                      show Contents
                    </button>
                  ) : null}
                </span>
                <span>
                  {!appearanceRailOpen ? (
                    <button
                      type="button"
                      className="rounded-sm border border-[var(--wiki-border)] bg-[var(--wiki-subtle)] px-2 py-1 text-[var(--wiki-link)] hover:underline"
                      aria-label="Show Appearance panel"
                      aria-controls="md-wiki-appearance-panel"
                      onClick={() => setAppearanceRailOpen(true)}
                    >
                      show Appearance
                    </button>
                  ) : null}
                </span>
              </div>
            ) : null}

            {!editorActive ? (
              <details className="mb-4 border-y border-[var(--wiki-border)] py-2 text-[14px] xl:hidden">
                <summary className="cursor-pointer font-semibold">Contents</summary>
                <div className="pt-2"><WikiContents items={contents} mode={mode} /></div>
              </details>
            ) : null}

            <WorkspaceSection kind="main">
              {mode === 'msg' ? (
                <WikiMainArticle contentRef={contentRef} firstAnswerHref={firstAnswerHref} />
              ) : null}
              {mode === 'answers' ? (
                <div id="wiki-top">
                  <RouteHeading id="wiki-answers">Answers — {title}</RouteHeading>
                  <SubsToolbar />
                  <AnswersPanel />
                </div>
              ) : null}
              {mode === 'submsges' ? (
                <div id="wiki-top">
                  <RouteHeading id="wiki-submsges">Messages — {title}</RouteHeading>
                  <MessagesPanel />
                </div>
              ) : null}
            </WorkspaceSection>

            <WorkspaceSection kind="editor">
              <WikiEditorTemplate
                active={editorActive}
                editorMode={isEditorOpen ? editorMode : ''}
                settingsOpen={settingsOpen}
                onCloseSettings={onCloseSettings}
              />
            </WorkspaceSection>
            <WorkspaceSection kind="attachments"><div id="wiki-attachments"><WikiMainAttachments /></div></WorkspaceSection>
            <WorkspaceSection kind="favs"><div id="wiki-favs"><MenuSubsPanels mode="favs" /></div></WorkspaceSection>
            <WorkspaceSection kind="trash"><div id="wiki-trash"><MenuSubsPanels mode="trash" /></div></WorkspaceSection>
            <WorkspaceSection kind="drafts"><div id="wiki-drafts"><MenuSubsPanels mode="drafts" /></div></WorkspaceSection>

            {!editorActive ? (
              <details className="mt-8 border-y border-[var(--wiki-border)] py-2 text-[14px] xl:hidden">
                <summary className="cursor-pointer font-semibold">Appearance</summary>
                <div className="pt-2">
                  <WikiAppearance appearance={appearance} setAppearance={setAppearance} />
                </div>
              </details>
            ) : null}
          </main>

          <aside id="md-wiki-appearance-panel" {...legacyHookAttrs('page.rightRail', showAppearanceRail ? 'hidden min-w-0 xl:block' : 'hidden')}>
            {showAppearanceRail ? (
              <WikiAppearance
                appearance={appearance}
                setAppearance={setAppearance}
                onHide={() => setAppearanceRailOpen(false)}
              />
            ) : null}
          </aside>
        </div>

        <footer {...legacyHookAttrs('page.footer', `mx-auto w-full ${widthClass} border-t border-[var(--wiki-border)] bg-[var(--wiki-bg)] px-8 py-5 text-[12px] text-[var(--wiki-muted)]`)}>
          MsgDesk Wiki
        </footer>
      </div>
      <QuickReply />
      <EnterPinForm />
      <LoadingOverlay />
      <PinDialog />
    </>
  )
}
